<template>
  <div>
    <div
      v-show="displayAnnouncement"
      id="GlobaleFreeShippingBannerContainer"
      ref="announcement"
      class="announcement"
      :class="{
        [stickyClass]: announcement.isSticky,
        [hiddenClass]: announcement.isHidden,
      }"
      @transitionend="handleTransitionEnd"
    >
      <announcement-bar
        v-if="!isInternationalLocale"
        :message="announcementText"
        :announcement-link="announcementLink"
      />
    </div>

    <header
      :id="liquid.sectionId"
      ref="siteHeader"
      class="site-header"
      :class="{
        ...headerClasses,
        [stickyClass]: header.isSticky,
        'is-cart-drawer-open': header.isCartDrawerOpen,
        'is-mobile-menu-open': header.isMobileMenuOpen,
        [hiddenClass]: header.isHidden,
      }"
      @transitionend="handleTransitionEnd"
    >
      <site-header-mobile
        v-if="isMobile"
        :logo-object="logoObject"
        :alt-logo-object="altLogoObject"
        :logo-src="logoSrc"
        :alt-logo-src="altLogoSrc"
        :logo-text="logoText"
        :wishlist-url="wishlistLink"

        :enable-header-cart="enableHeaderCart"
        :enable-header-search="enableHeaderSearch"
        :enable-header-wishlist="enableHeaderWishlist"
        :enable-header-account="enableHeaderAccount"
        :enable-mobile-carousel="enableMobileCarousel"
        :enable-link-background="enableLinkBackground"

        :breadcrumb="breadcrumb"
        :mobile-top-carousel="mobileTopCarousel"
        :main-menu="mainMenu"
        :section-blocks="mobileBlocks"
        :is-search-opened="isSearchOpened"

        @on-mobile-menu-toggle="handleMobileMenuToggle"
      />
      <site-header-desktop
        v-if="!isMobile"
        :logo-object="logoObject"
        :alt-logo-object="altLogoObject"
        :logo-src="logoSrc"
        :alt-logo-src="altLogoSrc"
        :logo-text="logoText"
        :wishlist-url="wishlistLink"

        :enable-header-cart="enableHeaderCart"
        :enable-header-search="enableHeaderSearch"
        :enable-header-wishlist="enableHeaderWishlist"
        :enable-header-account="enableHeaderAccount"
        :enable-link-background="enableLinkBackground"
        :enable-megamenu="enableMegamenu"

        :main-menu="mainMenu"
        :section-blocks="desktopBlocks"
        :is-search-opened="isSearchOpened"
      />
      <search-modal
        v-if="enableHeaderSearch && showLazyLoaded"
        @on-search-toggle="onSearchToggle"
      />
    </header>
  </div>
</template>
<script>

/**
 * Section: Site Header
 * ------------------------------------------------------------------------------
 * @param {String}  [liquid.sectionId] - Section's Liquid ID.
 *
 * @param {Boolean} [showAnnouncement] - Setting for enabling announcement bar
 * @param {String}  [announcementLink] - URL link for announcement message
 * @param {String}  [announcementTextDesktop] - Announcement bar message that showing on Desktop
 * @param {String}  [announcementTextMobile] - Announcement bar message that showing on Mobile
 * @param {Boolean} [announcementHomePageOnly] - Setting for showing announcement bar only on Homepage.
 * @param {Boolean} [enableStickyAnnouncement] - Enabling sticky announcement feature.
 *
 * @param {Boolean} [enableStickyHeader] - Global setting for enabling sticky header.
 * @param {Boolean} [enableHeaderAccount] - Setting for enabling header account.
 * @param {Boolean} [enableHeaderCart] - Setting for enabling header cart.
 * @param {Boolean} [enableHeaderSearch] - Setting for enabling header search.
 * @param {Boolean} [enableHeaderWishlist] - Setting for enabling header wishlist.
 * @param {Boolean} [enableMobileCarousel] - Setting for enabling header collection menu slider on Mobile Menu.
 * @param {Boolean} [enableMegamenu] - Setting for enabling header Megamenu.
 * @param {Boolean} [enableLinkBackground] - Setting for enabling background styling for highlighted links.
 *
 * @param {String}  [wishlistUrl] - Link to the Wishlist page.
 * @param {String}  [headerBackdrop] - Setting for the header appearance styles.
 * @param {String}  [stickyHeaderStyle] - Sticky header style either dark or light.
 *
 * @param {Object}  [logoObject] - A formatted image logo.
 * @param {Object}  [altLogoObject] - A formatted alternative image logo.
 * @param {String}  [logoSrc] - Logo file src with responsive size (max-width from settings).
 * @param {String}  [altLogoSrc] - Alternative logo src with responsive size.
 * @param {String}  [logoText] - Text (SVG) setting for header logo.
 *
 * @param {Object}  [breadcrumb] - An object, that contains collection URL and title for breadcrumb that is shown only on PDP.
 * @param {Array}   [mobileTopCarousel] - Formatted nested linklist with collections links and images inside.
 * @param {Object}  [mainMenu] - Formatted nested linklist.
 * @param {Array}   [sectionBlocks] - Formatted section blocks array.
 *
 */
import { mapState } from 'pinia'

import { defineAsyncComponent } from 'vue'
import { nextTick } from 'vue'

import { useScreenStore } from '~stores/screen'
import { useLocaleStore } from '~stores/locale'

import { waitForElem } from '~/helpers/dom'
import classes from '~/helpers/classes'

import AnnouncementBar from '~components/site-header-components/announcement-bar/announcement-bar'
import SiteHeaderMobile from '~components/site-header-components/site-header-mobile/site-header-mobile'
import SiteHeaderDesktop from '~components/site-header-components/site-header-desktop/site-header-desktop'

export default {
  name: 'SiteHeader',

  components: {
    AnnouncementBar,
    SiteHeaderMobile,
    SiteHeaderDesktop,
    SearchModal: defineAsyncComponent(() =>
      import('~legacy-vue/SearchModal'),
    ),
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    showAnnouncement: {
      type: Boolean,
      default: false,
    },
    enableStickyAnnouncement: {
      type: Boolean,
      default: false,
    },
    announcementLink: {
      type: String,
      default: '',
    },
    announcementTextMobile: {
      type: String,
      default: '',
    },
    announcementTextDesktop: {
      type: String,
      default: '',
    },
    announcementHomePageOnly: {
      type: Boolean,
      default: false,
    },
    enableStickyHeader: {
      type: Boolean,
      default: true,
    },
    enableHeaderAccount: {
      type: Boolean,
      default: false,
    },
    enableHeaderCart: {
      type: Boolean,
      default: false,
    },
    enableHeaderSearch: {
      type: Boolean,
      default: false,
    },
    enableHeaderWishlist: {
      type: Boolean,
      default: false,
    },
    enableMobileCarousel: {
      type: Boolean,
      default: false,
    },
    enableMegamenu: {
      type: Boolean,
      default: false,
    },
    enableLinkBackground: {
      type: Boolean,
      default: false,
    },
    wishlistUrl: {
      type: [String],
      default: '',
    },
    headerBackdrop: {
      type: String,
      default: '',
    },
    stickyHeaderStyle: {
      type: String,
      default: '',
    },
    logoObject: {
      type: [Boolean, Object],
      default: false,
    },
    altLogoObject: {
      type: [Boolean, Object],
      default: false,
    },
    logoSrc: {
      type: String,
      default: '',
    },
    altLogoSrc: {
      type: String,
      default: '',
    },
    logoText: {
      type: String,
      default: '',
    },
    breadcrumb: {
      type: [Boolean, Object],
      default: false,
    },
    mobileTopCarousel: {
      type: Array,
      default: () => [],
    },
    menuLinks: {
      type: Array,
      default: () => [],
    },
    sectionBlocks: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      header: {
        initialHeight: 48,
        height: 48,
        isSticky: false,
        isMobileMenuOpen: false,
        isCartDrawerOpen: false,
        isHidden: false,
      },
      announcement: {
        initialHeight: 32,
        height: 32,
        isSticky: false,
        geBannerSelector: '.fsbloaded', // Global-E adds this class when the FreeShippingBanner banner is loaded,
        isHidden: false,
      },
      collectionToolbar: null,
      isSearchOpened: false,
      showLazyLoaded: false,
      stickyOffset: 0,
    }
  },

  computed: {

    /**
     * Map global state.
     */
    ...mapState(useScreenStore, {
      isMobile: 'mobile',
      viewportWidth: 'width',
    }),

    /**
     * Map locale state.
     */
    ...mapState(useLocaleStore, {
      countryCode: 'countryCode',
    }),

    /**
     * Calculate text for announcement message
     * @returns {String}
     */
    announcementText() {
      if (!this.isMobile) {
        return this.announcementTextDesktop
      }

      return this.announcementTextMobile
    },

    /**
     * Calculates whether we are on an international locale
     * @returns {Boolean}
     */
    isInternationalLocale() {
      return this.countryCode !== 'GB'
    },

    /**
     * Calculate if to show announcement bar based on the settings and page type.
     * @returns {Boolean}
     */
    displayAnnouncement() {
      return this.showAnnouncement
      && (this.announcementText || this.isInternationalLocale)
      && (!this.announcementHomePageOnly || mint.request.pageType === 'index')
    },

    /**
     * Compute sticky class that comes from `classes` object to be able to use
     * it in `template` and `:class` bindings
     * @returns {String}
     */
    stickyClass() {
      return classes.sticky || 'is-sticky'
    },

    /**
     * Compute `hidden` class which differs from the usual `is-hidden`.
     * - It's added for smoother transition when window is scrolled, but element
     * isn't sticky
     * @returns {String}
     */
    hiddenClass() {
      return 'not-visible'
    },

    /**
     * Calculate header style classes based on the settings.
     * @returns {Object}
     */
    headerClasses() {
      const headerBackdropClass = this.headerBackdrop

      return {
        'transparent': mint.request.pageType === 'index',
        [headerBackdropClass]: headerBackdropClass,
        [this.stickyHeaderStyle]: this.stickyHeaderStyle,
      }
    },

    /**
     * Calculate section blocks related to the desktop menu.
     * @returns {Array}
     */
    desktopBlocks() {
      return this.sectionBlocks.filter(block => block.type.includes('style_'))
    },

    /**
     * Calculate section blocks related to the mobile menu.
     * @returns {Array}
     */
    mobileBlocks() {
      return this.sectionBlocks.filter(block => block.type === 'mobile_images')
    },

    /**
     * Calculate section blocks that replace link text with images.
     * @returns {Array}
     */
    linkedImageBlocks() {
      return this.sectionBlocks.filter(block => block.type === 'linked_image')
    },

    /**
     * Attach image block data to navigation links.
     * @returns {Array}
     */
    mainMenu() {
      return this.menuLinks.map(link => {
        for (const imageBlock of this.linkedImageBlocks) {
          if (
            !imageBlock.heading ||
            !imageBlock.content ||
            imageBlock.heading !== link.title
          ) {
            continue
          }

          link.image = imageBlock
        }

        return link
      })
    },

    /**
     * Calculates threshold when the sticky state should be added
     * @returns {Number}
     */
    stickyThreshold() {
      return (this.header.initialHeight + this.announcement.initialHeight)
    },

    /**
     * Calculates whether the parent section should be static.
     * @returns {Boolean}
     */
    isStatic() {
      return !this.enableStickyHeader && !this.enableStickyAnnouncement
    },

    /**
     * Calculates the URL to the wishlist page.
     * @returns {String}
     */
    wishlistLink() {
      return this.wishlistUrl
        ? this.wishlistUrl
        : `${mint.routes.normalisedRoot}pages/wish-list`
    },
  },

  watch: {

    /**
     * Watching the `isSearchOpened` prop. If the value is true and mobile menu
     * is opened as well, then the menu should be closed to show search overlay.
     */
    isSearchOpened(newValue) {
      newValue ? this.bodyLock() : this.bodyUnlock()
    },

    /**
     * The user has resized their screen.
     */
    viewportWidth() {
      nextTick(() => {
        this.setInitialHeights()
        this.calculateCurrentHeights()
      })
    },

    /**
     * The `mobile` state has changed (if you switch the iPad orientation etc.)
     */
    isMobile(newValue) {
      if (newValue) {
        return
      }

      this.header.isMobileMenuOpen = false
    },
  },

  mounted() {
    this.resetCriticalStyles()
    this.defineStickySection()
    this.setInitialHeights()
    this.calculateCurrentHeights()
    this.initializeAnnouncement()
    this.setCollectionToolbar()
    this.setEventListeners()
    this.toggleLazyLoaded()
  },

  methods: {

    /**
     * Function which set global css variables.
     * @param {String} name - Variable name (should start from '--')
     * @param {String} value - Variable value (with units, e.g '100px')
     */
    setGlobalVariable(name, value) {
      document.documentElement.style.setProperty(`${name}`, `${value}`)
    },

    /**
     * Function which sets global css variables based on data values
     */
    setDimensions() {
      this.setGlobalVariable(
        '--announcement-bar-height',
        `${Math.floor(this.announcement.height)}px`,
      )

      this.setGlobalVariable(
        '--header-height',
        `${Math.floor(this.header.height)}px`,
      )

      this.setGlobalVariable(
        '--header-height-total',
        `${Math.floor(this.announcement.height + this.header.height)}px`,
      )
    },

    /**
     * Set the header and announcement initial (non-sticky) height based on
     * their first-loaded/resized `offsetHeight`.
     * - This is done to preserve height values, which can be changed
     * while scrolling and prevent any flicks/glitches when elements become sticky
     * and change height at the same time.
     */
    setInitialHeights() {
      this.announcement.initialHeight = this.$refs.announcement.offsetHeight || 0
      this.header.initialHeight = this.$refs.siteHeader.offsetHeight || 0
    },

    /**
     * Function which calculates the header and announcement height based on their
     * `offsetHeight`, should also be called when some event (e.g resize) is
     * triggered.
     */
    calculateCurrentHeights() {
      this.announcement.height = this.$refs.announcement.offsetHeight || 0
      this.header.height = this.$refs.siteHeader.offsetHeight || 0

      /**
       * If the sticky announcement is disabled, then we suppose that
       * height should always be 0 - to make the menu stick to the top
       */
      if (!this.displayAnnouncement
      || (!this.enableStickyAnnouncement && window.scrollY > this.stickyThreshold)) {
        this.announcement.height = 0
      }

      /**
       * If the sticky header is disabled, then header height should be 0 on
       * scroll
       */
      if (!this.enableStickyHeader && window.scrollY > this.stickyThreshold) {
        this.header.height = 0
      }

      this.setDimensions()
    },

    /**
     * If the sticky announcement enabled, add the sticky class to the banners
     */
    setScrollState() {
      this.collectionToolbar?.classList.add(classes.sticky)

      this.enableStickyHeader
        ? this.header.isSticky = true
        : this.header.isHidden = true

      this.enableStickyAnnouncement
        ? this.announcement.isSticky = true
        : this.announcement.isHidden = true
    },

    /**
     * If the sticky announcement enabled, remove the sticky class from the banners
     */
    removeScrollState() {
      this.header.isHidden = false
      this.announcement.isHidden = false
      this.header.isSticky = false
      this.announcement.isSticky = false
      this.collectionToolbar?.classList.remove(classes.sticky)
    },

    /**
     * Wait till GE banner appears, then set the height variables.
     */
    initializeAnnouncement() {
      if (!this.displayAnnouncement || !this.isInternationalLocale) {
        // nothing to do
        return
      }

      waitForElem(this.announcement.geBannerSelector).then((geBanner) => {
        geBanner.firstChild?.style?.removeProperty('padding')

        this.setInitialHeights()
        this.calculateCurrentHeights()
      })
    },

    /**
     * Handles opening hamburger menu
     * @param {Boolean} isOpened - emitted value that indicates hamburger open state
     */
    handleMobileMenuToggle(isOpened) {
      isOpened
        ? this.bodyLock()
        : this.bodyUnlock()

      mint.EventBus.dispatch('cartDrawer:close')
      this.header.isMobileMenuOpen = isOpened
      this.calculateCurrentHeights()
    },

    /**
     * Set Event listeners.
     */
    setEventListeners() {
      mint.EventBus.on('cartDrawer:open', () => {
        this.header.isCartDrawerOpen = true
      })

      mint.EventBus.on('cartDrawer:close', () => {
        this.header.isCartDrawerOpen = false
      })

      if (this.isStatic) {
        return
      }

      window.addEventListener(
        'scroll',
        () => {
          if (window.scrollY > this.stickyThreshold) {
            this.setScrollState()
          }

          /**
           * An additional custom threshold condition is necessary for the
           * following reasons (mainly - due to changes in the header height):
           * - When the full threshold is passed, the header becomes a sticky
           * (including the announcement, but it does not change the height) and
           * vice versa when returning to the top
           * - At the usual scroll or scrolling down it works okay
           * - However, when scrolling slowly back up, there is a place at the
           * junction of the transition where the header hangs in an implicit state
           * - This causes the height change and the toggling `sticky` state stack
           * to loop
           */
          else if (document.body.style.overflow !== 'hidden' && window.scrollY <= this.stickyThreshold / 2) {
            this.removeScrollState()
          }
        },
        { passive: true },
      )
    },

    /**
     * Make body scroll totally locked, including the iOS devices.
     * - Using when mobile menu is opened etc.
     * - Note: any CSS solution other than a `fixed` position on body still works
     * incorrectly on iOS.
     */
    bodyLock() {
      document.body.classList.add(classes.locked)
    },

    /**
     * Unblock body to be able to continue scrolling.
     */
    bodyUnlock() {
      document.body.classList.remove(classes.locked)
    },

    /**
     * A method that is called when the search modal is toggled.
     *
     * @param {Boolean} isSearchOpened - Emitted value, which indicates the
     * current state of search overlay
     */
    onSearchToggle(isSearchOpened) {
      isSearchOpened
        ? this.bodyLock()
        : this.bodyUnlock()

      this.isSearchOpened = isSearchOpened
    },

    /**
     * Load async components after initial load.
     * - Currently renders only `<search-modal>` component. Since the clicks
     * are handled inside this component, we can't change `v-if` condition on
     * click to render it asynchronously, as we are waiting for the event emits.
     * So we just postpone the loading for now.
     */
    toggleLazyLoaded() {
      setTimeout(() => {
        this.showLazyLoaded = true
      }, 1000)
    },

    /**
     * Change data `collectionToolbar` with DOM node if we are on the
     * collection page.
     */
    setCollectionToolbar() {
      if (mint.request.pageType !== 'collection') {
        return
      }

      this.collectionToolbar = document.querySelector('.toolbar')
    },

    /**
     * Sets the offset once when the header/announcement becomes sticky.
     * - this is done to avoid repeating recalculations every time you scroll,
     * since they are visible due to the transition when there are another
     * sticky elements on the page
     */
    setStickyOffset() {
      this.stickyOffset = 0

      if (this.isStatic) {
        this.setGlobalVariable(
          '--header-sticky-offset',
          `${this.stickyOffset}px`,
        )

        return
      }

      /* Nothing to calculate in non-sticky states */
      if (window.scrollY < this.stickyThreshold) {
        return
      }

      this.stickyOffset += this.announcement.isSticky ? this.announcement.height : 0
      this.stickyOffset += this.header.isSticky ? this.header.height : 0

      this.setGlobalVariable(
        '--header-sticky-offset',
        `${this.stickyOffset}px`,
      )
    },

    /**
     * Used when the site-header is loaded.
     * - Since on the parent section `data-mint-section-state='active'` is
     * triggered before the section is mounted, content shifts are still exist
     * - Therefore, need to add a class to reset them only when the section is
     * fully loaded
     */
    resetCriticalStyles() {
      const parentSection = this.$refs.siteHeader.closest(
        '[data-mint-section="site-header"]',
      )

      parentSection?.classList.add('is-mounted')
    },

    /**
     * If there is no sticky option neither for header nor for announcement,
     * then add non-sticky class to the parent section.
     */
    defineStickySection() {
      if (!this.isStatic) {
        return
      }

      const parentSection = this.$refs.siteHeader.closest(
        '.section-site-header',
      )

      parentSection?.classList.add('is-static')
    },

    /**
     * Wait until the transition is over and recalculate the heights.
     * - this is done because the `setTimeout` worked inconsistently
     * - also to reduce delays between calculations without significantly
     * impacting performance (if to do it on 'scroll' event)
     * @param {Event} event - transition HTML Event.
     */
    handleTransitionEnd(event) {
      const affectedProperties = [
        'height', 'max-height', 'min-height', 'padding', 'padding-top', 'padding-bottom', 'transform', 'flex-basis',
      ]

      if (!affectedProperties.includes(event?.propertyName)) {
        return
      }

      this.calculateCurrentHeights()
      this.setStickyOffset()
    },
  },
}
</script>

<style src="./site-header.scss" lang="scss"></style>
